import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMeasureElement } from '@wix/da-hooks/pkg/useMeasureElement/useMeasureElement';
import { getCoreLevel } from '@wix/da-shared-react/pkg/publicSession/selectors';
import UserAvatar from '@wix/da-shared-react/pkg/User/Avatar';
import UserLink from '@wix/da-shared-react/pkg/User/Link';
import { formatDate, DATE_FORMATS } from '@wix/da-i18next/pkg/time/functions';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import ReportsSection from '../ReportsSection';
import ReportsTable from '../ReportsTable';
import CoreUpsellRow from '../ReportsTable/CoreUpsellRow';
import TopSubscribersActionMenu from './TopSubscribersActionMenu';
import { TimePeriod, TimePeriodOption } from '../../../types/reports';
import { PapiStudioSellerStatsInit } from '@wix/da-papi-types';
import { sanitizeHref } from '@wix/da-url';

import s from './TopSubscribersSection.scss';

export interface Props {
  className?: string;
  data?: PapiStudioSellerStatsInit['topSubscribers'];
}

const EXPANDED_ACTIONS_TABLE_WIDTH = 1160;

const TopSubscribersSection: FC<Props> = ({ data, className }) => {
  const { t, i18n } = useTranslation();
  const { ref: tableRef, dimensions: tableDimensions } = useMeasureElement({
    throttle: 250,
    throttleOptions: { trailing: true },
  });
  const [expandedActions, setExpandedActions] = useState<boolean>(false);

  useEffect(() => {
    setExpandedActions(
      typeof tableDimensions.width === 'number' &&
        Math.floor(tableDimensions.width) >= EXPANDED_ACTIONS_TABLE_WIDTH
    );
  }, [tableDimensions.width]);

  const userCoreLevel = useSelector(getCoreLevel);
  const hasCoreProPlus = userCoreLevel === 'core_pro_plus' || userCoreLevel === 'core_max';

  const timePeriodOptions: TimePeriodOption[] = useMemo(
    () => [
      {
        value: TimePeriod.ALL,
        translationKey: `studio.reports.subscriberStates.${TimePeriod.ALL}`,
      },
      {
        value: TimePeriod.ACTIVE,
        translationKey: `studio.reports.subscriberStates.${TimePeriod.ACTIVE}`,
      },
      {
        value: TimePeriod.CANCELLED,
        translationKey: `studio.reports.subscriberStates.${TimePeriod.CANCELLED}`,
      },
      {
        value: TimePeriod.NEW,
        translationKey: `studio.reports.subscriberStates.${TimePeriod.NEW}`,
      },
    ],
    []
  );

  const columns = useMemo(() => {
    return [
      {
        key: 'subscriber',
        label: t('studio.reports.section.topSubscribers.table.header.subscriber'),
      },
      {
        key: 'first_join_date',
        label: t('studio.reports.section.topSubscribers.table.header.firstJoinDate'),
      },
      {
        key: 'last_cancel_date',
        label: t('studio.reports.section.topSubscribers.table.header.lastCancelDate'),
      },
      {
        key: 'months_subscribed',
        label: t('studio.reports.section.topSubscribers.table.header.cycles'),
      },
      {
        key: 'sales_volume',
        label: t('studio.reports.section.topSubscribers.table.header.sales'),
      },
      {
        key: 'actions',
        label: t('studio.reports.section.topSubscribers.table.header.actions'),
      },
    ];
  }, [t]);

  if (!data) {
    return null;
  }

  return (
    <ReportsSection
      reportType="topSubscribers"
      className={className}
      title={t('studio.reports.section.topSubscribers.title')}
      description={t('studio.reports.section.topSubscribers.description', {
        postProcess: ['reactPostprocessor'],
        learnMoreLink: (
          <a
            className="text-link-level-2-tertiary"
            href={sanitizeHref(
              t('studio.reports.section.topSubscribers.description.learnMoreLink.url')
            )}
          >
            {t('studio.reports.section.topSubscribers.description.learnMoreLink.text')}
          </a>
        ),
      })}
      data={data}
      timePeriodOptions={timePeriodOptions}
      dataFilter={dataToFilter =>
        /* show top 50 subscribers for core members */
        dataToFilter?.slice(0, hasCoreProPlus ? 50 : 3)
      }
      renderTable={(filteredData: any[]) => (
        <ReportsTable
          tableRef={tableRef}
          columns={columns}
          data={filteredData}
          initialVisibleCount={5}
          renderRow={(
            {
              user,
              deviation,
              subscriptionStatus,
              firstJoinDate,
              lastCancelDate,
              monthsSubscribed,
              salesVolume,
            }: any,
            index: number
          ) => {
            const isActive = subscriptionStatus === 'Active';

            return (
              <>
                <td>
                  <div className={s['subscriber']}>
                    <UserAvatar className={s['avatar']} user={user} size={40} />
                    <div className={s['subscriber-info']}>
                      <UserLink user={user} className={s['userlink']} />
                      <div className={s['deviation-title']}>{deviation?.title || '---'}</div>
                      <div className={s[isActive ? 'status-active' : 'status-cancelled']}>
                        {t(
                          isActive
                            ? 'studio.reports.section.topSubscribers.status.active'
                            : 'studio.reports.section.topSubscribers.status.cancelled'
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  {firstJoinDate
                    ? formatDate(firstJoinDate, DATE_FORMATS.Date.Full, i18n.language)
                    : '---'}
                </td>
                <td>
                  {lastCancelDate
                    ? formatDate(lastCancelDate, DATE_FORMATS.Date.Full, i18n.language)
                    : '---'}
                </td>
                <td>{monthsSubscribed || '---'}</td>
                <td>{salesVolume ? formatDollars(salesVolume) : '---'}</td>
                <td>
                  <TopSubscribersActionMenu
                    user={user}
                    wrapperClassName={s['menu-wrapper']}
                    expandedActions={expandedActions}
                  />
                </td>
              </>
            );
          }}
          renderFooter={() => !hasCoreProPlus && <CoreUpsellRow />}
        />
      )}
    />
  );
};

export default TopSubscribersSection;
